import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap";
import "./ProfilePage.scss";
import CustomNavbarClients from "../navBarClients/CustomNavbarClients";
import ClientMenuBar from "../ClientMenuBar";
import Footer from "../Footer";
import UserProfileSidebar, { ProfileTab } from "./UserProfileSidebar";
import UserProfileContent from "./UserProfileContent";
import { useAuthStore } from "../../../../zustand/loginStore";
import LoadingScreen from "components/general/LoadingScreen";
import {
  fetchUserProfile,
  createAuthenticatedAxiosInstance,
  updateUserProfile,
} from "../../../../api/auth";
import { UserProfile } from "interfaces/IUser";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useBreakpoints } from "hooks/useBreakPoints";
import { useUserStore } from "../../../../zustand/userStore";

/**
 * Página principal de perfil de usuario
 * Muestra información personal, pedidos y favoritos del usuario
 */
const ProfilePage: React.FC = () => {
  const { token, isLoggedIn } = useAuthStore();
  const location = useLocation();
  const section = location.state;
  const { isMobile } = useBreakpoints();
  const [activeTab, setActiveTab] = useState<ProfileTab>(ProfileTab.PROFILE);
  const [userData, setUserData] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showProfileContent, setShowProfileContent] = useState<boolean>(true);
  const navigate = useNavigate();
  const { imageTimestamp, setImageTimestamp } = useUserStore();

  const loadUserProfile = async () => {
    // Solo cargar si el usuario está logueado
    if (!isLoggedIn()) {
      setError("Debe iniciar sesión para ver el perfil");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const axiosInstance = createAuthenticatedAxiosInstance(token);
      const profileData = await fetchUserProfile(axiosInstance);

      // Verificar si la respuesta tiene la estructura esperada
      if (profileData && profileData.user) {
        setUserData(profileData.user);
      } else {
        setError("Formato de datos de perfil inesperado");
      }
    } catch (error) {
      console.error("Error al cargar perfil:", error);
      setError("No se pudo cargar la información del perfil");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadUserProfile();
  }, [token, isLoggedIn, isAddingAddress, isEditing]);

  // Función para manejar actualizaciones del perfil
  const handleProfileUpdate = (updatedProfile: UserProfile) => {
    setUserData(updatedProfile);
  };

  // Función para obtener las iniciales del usuario
  const getUserInitials = (name: string): string => {
    if (!name) return "";
    const nameParts = name.split(" ");
    const firstInitial = nameParts[0]
      ? nameParts[0].charAt(0).toUpperCase()
      : "";
    const lastInitial =
      nameParts.length > 1
        ? nameParts[nameParts.length - 1].charAt(0).toUpperCase()
        : "";
    return firstInitial + lastInitial;
  };

  // Función para manejar el cambio de foto en móvil
  const handlePhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !userData) return;

    // Verificar tipo de archivo
    const fileType = file.type;
    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(fileType)) {
      alert('Solo se permiten archivos JPG, JPEG o PNG');
      return;
    }

    try {
      // Convertir la imagen a base64
      const base64Image = await convertToBase64(file);
      
      // Crear objeto de datos para la actualización
      const updateData = {
        name: userData.name,
        email: userData.email,
        phone: userData.phone || '',
        image: base64Image
      };
      
      // Enviar la actualización al servidor
      const axiosInstance = createAuthenticatedAxiosInstance(token);
      const updatedProfile = await updateUserProfile(axiosInstance, updateData);
      
      // Actualizar el estado local
      if (updatedProfile && updatedProfile.user) {
        setUserData(updatedProfile.user);
        
        // También actualizar el store de autenticación para que el navbar se actualice
        const { setUserData: setAuthUserData } = useAuthStore.getState();
        setAuthUserData(updatedProfile.user);
        
        // Forzar la actualización de la imagen actualizando el timestamp
        setImageTimestamp(Date.now());
      }
      
    } catch (error) {
      console.error("Error al subir la imagen:", error);
      alert("No se pudo actualizar la imagen de perfil. Inténtelo de nuevo.");
    }
  };

  // Función para convertir un archivo a base64
  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Extraer solo la parte base64 sin el prefijo (data:image/jpeg;base64,)
        const base64String = result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  /**
   * Loads the clicked tab content
   */
  const onTabClick = (tab: ProfileTab) => {
    switch (tab) {
      case ProfileTab.PROFILE: {
        setShowProfileContent(true);
        setActiveTab(tab);
        navigate("");
        break;
      }
      case ProfileTab.ORDERS: {
        setShowProfileContent(false);
        setActiveTab(tab);
        navigate("orders");
        break;
      }
      case ProfileTab.FAVORITES: {
        setShowProfileContent(false);
        setActiveTab(tab);
        navigate("favorites");
        break;
      }
      default:
        setShowProfileContent(true);
        break;
    }
  };

  useEffect(() => {
    const selectedTab = section as ProfileTab;
    console.log(selectedTab);
    if (selectedTab) {
      onTabClick(selectedTab);
    }
  }, [section]);

  // Additional effect to handle direct URL navigation and set correct tab
  useEffect(() => {
    const currentPath = location.pathname;
    if (currentPath.includes("/orders")) {
      setActiveTab(ProfileTab.ORDERS);
      setShowProfileContent(false);
    } else if (currentPath.includes("/favorites")) {
      setActiveTab(ProfileTab.FAVORITES);
      setShowProfileContent(false);
    } else if (currentPath === "/profile" || currentPath === "/profile/") {
      setActiveTab(ProfileTab.PROFILE);
      setShowProfileContent(true);
    }
  }, [location.pathname]);

  // Si está cargando, mostrar pantalla de carga
  if (isLoading) {
    return <LoadingScreen loadingText="Cargando perfil..." />;
  }

  // Si hay un error, mostrar mensaje
  if (error) {
    return (
      <div className="profile-page">
        <CustomNavbarClients />
        <ClientMenuBar />

        <Col className="profile-container ">
          <div className="text-center p-5">
            <h3>{error}</h3>
            <p>
              Por favor, inténtelo nuevamente más tarde o contacte a soporte.
            </p>
            <button
              className="btn btn-primary mt-3"
              onClick={() => navigate("/login")}
            >
              Iniciar sesión
            </button>
          </div>
        </Col>

        <Footer />
      </div>
    );
  }

  // Si tenemos datos, mostrar el perfil
  return (
    <div className="profile-page">
      <CustomNavbarClients />
      <ClientMenuBar />

      <Col className="profile-container">
        {/* Layout móvil: saludo arriba, foto y botones abajo */}
        {isMobile && (
          <>
            {/* Saludo en la parte superior */}
            <div className="mobile-greeting">
              <h2 className="greeting-text">
                ¡Hola, {userData?.name || "Usuario"}!
              </h2>
            </div>

            {/* Foto y botones */}
            <div className="mobile-profile-header">
              <div className="profile-photo-wrapper">
                <div
                  className="profile-photo-container"
                  onClick={() => document.getElementById("photo-input")?.click()}
                  style={{ cursor: "pointer" }}
                >
                  {userData?.image_url ? (
                    <img
                      src={`${userData.image_url}?t=${imageTimestamp}`}
                      alt="Foto de perfil"
                      className="profile-photo"
                    />
                  ) : (
                    <div className="profile-photo-placeholder">
                      {getUserInitials(userData?.name || "")}
                    </div>
                  )}
                </div>
                <div className="camera-icon" onClick={() => document.getElementById("photo-input")?.click()}>+</div>
              </div>
              <div className="mobile-tabs-container">
                <button
                  className={`mobile-tab ${
                    activeTab === ProfileTab.PROFILE ? "active" : ""
                  }`}
                  onClick={() => onTabClick(ProfileTab.PROFILE)}
                >
                  Mi Perfil
                </button>
                <button
                  className={`mobile-tab ${
                    activeTab === ProfileTab.ORDERS ? "active" : ""
                  }`}
                  onClick={() => onTabClick(ProfileTab.ORDERS)}
                >
                  Mis Pedidos
                </button>
                <button
                  className={`mobile-tab ${
                    activeTab === ProfileTab.FAVORITES ? "active" : ""
                  }`}
                  onClick={() => onTabClick(ProfileTab.FAVORITES)}
                >
                  Favoritos
                </button>
              </div>
            </div>

            {/* Input oculto para cambiar foto */}
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              style={{ display: "none" }}
              id="photo-input"
            />
          </>
        )}

        <Row>
          <Col lg={4} md={12} className="mb-4 mb-lg-0">
            <UserProfileSidebar
              onTabClick={onTabClick}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              userData={userData}
              onProfileUpdate={handleProfileUpdate}
            />
          </Col>

          <Col lg={8} md={12}>
            {showProfileContent ? (
              <UserProfileContent
                userData={userData}
                isAddingAddress={isAddingAddress}
                setIsAddingAddress={setIsAddingAddress}
                isEditing={isEditing}
                setIsEditing={setIsEditing}
              />
            ) : (
              <Outlet />
            )}
          </Col>
        </Row>
      </Col>
      <Footer />
    </div>
  );
};

export default ProfilePage;
